<template>
    <el-container class="list-page time-page">
        <el-header class="list-page-header time-page-header">
            <div>
                <h1 class="list-page-title">{{$t('time.pageTitle')}}</h1>
            </div>
            <div class="list-page-search">
                <el-input prefix-icon="el-icon-search" v-model="searchContent"
                          :disabled="!contextLoaded"
                          :placeholder="$t('time.searchPlaceholder')"></el-input>
            </div>
            <div class="time-header-actions">
                <el-button class="list-page-add-button" type="primary" :disabled="!contextLoaded || loading"
                           @click="goCreate">
                    <i class="el-icon-plus"></i><span>{{$t('time.addTime')}}</span>
                </el-button>
            </div>
        </el-header>
        <el-main class="list-page-main" v-loading="loading">
            <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <div v-else-if="contextLoaded && !loading && tableData.length === 0" class="time-empty">
                <i class="el-icon-time"></i>
                <h2>{{$t('time.noTime')}}</h2>
                <p>{{$t('time.noTimeDescription')}}</p>
            </div>
            <div v-else class="list-table-card time-desktop-table">
                <el-table class="list-table" :data="tableData" @row-click="handleRowOpen">
                    <el-table-column :label="$t('common.client')" min-width="240">
                        <template slot-scope="scope">
                            <strong class="time-client-name">{{scope.row.clientName}}</strong>
                            <span class="time-description">{{scope.row.timeDescription}}</span>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('common.date')" width="180">
                        <template slot-scope="scope"><span class="time-date-value">{{formatDate(scope.row.startDate)}}</span></template>
                    </el-table-column>
                    <el-table-column :label="$t('time.duration')" width="120">
                        <template slot-scope="scope">{{formatDuration(scope.row.totalMinutes)}}</template>
                    </el-table-column>
                    <el-table-column :label="$t('common.rate')" min-width="190" align="right">
                        <template slot-scope="scope">
                            <span class="time-rate-value" :title="(scope.row.showRateName === 1 ? scope.row.rateName + ' · ' : '') + money(scope.row.rate)">
                                <span v-if="scope.row.showRateName === 1">{{scope.row.rateName}} · </span>{{money(scope.row.rate)}}
                            </span>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('common.total')" width="190" align="right">
                        <template slot-scope="scope"><strong class="time-total-value" :title="money(scope.row.total)">{{money(scope.row.total)}}</strong></template>
                    </el-table-column>
                </el-table>
            </div>
            <div v-if="tableData.length > 0" class="time-mobile-list">
                <button v-for="time in tableData" :key="time.innerUUID" type="button"
                        class="time-mobile-card" @click="openTime(time, $event)">
                    <span class="time-mobile-heading">
                        <strong>{{time.clientName}}</strong>
                        <strong>{{money(time.total)}}</strong>
                    </span>
                    <span v-if="time.timeDescription !== ''" class="time-mobile-description">{{time.timeDescription}}</span>
                    <span class="time-mobile-facts">
                        <span><small>{{$t('common.date')}}</small>{{formatDate(time.startDate)}}</span>
                        <span><small>{{$t('time.duration')}}</small>{{formatDuration(time.totalMinutes)}}</span>
                        <span><small>{{$t('common.rate')}}</small><span class="time-mobile-rate-value"
                              :title="(time.showRateName === 1 ? time.rateName + ' · ' : '') + money(time.rate)"><span v-if="time.showRateName === 1">{{time.rateName}} · </span>{{money(time.rate)}}</span></span>
                    </span>
                </button>
            </div>
            <div v-if="totalCount > 0" class="list-pagination">
                <el-pagination
                        @size-change="changePageSize"
                        @current-change="changePage"
                        :current-page="currentPage"
                        :page-sizes="[50, 100, 200, 400]"
                        :page-size="pageSize"
                        layout="total, sizes, prev, pager, next, jumper"
                        :total="totalCount">
                </el-pagination>
            </div>
        </el-main>

    </el-container>
</template>

<script>
    import {debounce} from '@/utils/debounce'
    import {getBrowserTimeZone} from '@/utils/browserTimeZone'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {syncCompanySelection} from '@/utils/company'
    import {readListPagination, writeListPagination} from '@/utils/listPagination'
    import {readListQueryState, writeListQueryState} from '@/utils/listQueryState'
    import {formatTimeListDate, timeEpoch} from '@/utils/timeTracker'
    import {reportHandledError} from '@/utils/sentry'
    import {openListDetail} from '@/utils/listNavigation'

    export default {
        data() {
            return {
                company: null,
                contextLoaded: false,
                loading: true,
                loadError: '',
                rawTimes: [],
                searchContent: '',
                currentPage: 1,
                pageSize: 50,
                settings: {
                    timeSortType: null,
                    timeSortAscending: null
                },
                userSetting: null,
                timeQuerySequence: 0
            }
        },
        computed: {
            filteredTimes() {
                if (this.loadError !== '') {
                    return []
                }
                const companyUUID = this.company == null ? '' : this.company.innerUUID
                const keyword = this.searchContent.trim().toLocaleLowerCase()
                const filtered = this.rawTimes.filter(time => {
                    if (time.companyUUID !== companyUUID || time.syncStatus === 1) {
                        return false
                    }
                    if (keyword === '') {
                        return true
                    }
                    const clientName = time.clientName == null ? '' : String(time.clientName)
                    const description = time.timeDescription == null ? '' : String(time.timeDescription)
                    return clientName.toLocaleLowerCase().includes(keyword)
                        || description.toLocaleLowerCase().includes(keyword)
                })
                const sortByClient = this.settings.timeSortType === 0
                const direction = this.settings.timeSortAscending === 1 ? 1 : -1
                return filtered.slice().sort((left, right) => {
                    if (sortByClient) {
                        const clientOrder = String(left.clientName || '').localeCompare(String(right.clientName || ''), this.$i18n.locale)
                        if (clientOrder !== 0) {
                            return clientOrder * direction
                        }
                    }
                    const dateOrder = timeEpoch(left.startDate) - timeEpoch(right.startDate)
                    if (dateOrder !== 0) {
                        return dateOrder * direction
                    }
                    return String(left.innerUUID || '').localeCompare(String(right.innerUUID || '')) * direction
                })
            },
            totalCount() {
                return this.filteredTimes.length
            },
            tableData() {
                const startIndex = (this.currentPage - 1) * this.pageSize
                return this.filteredTimes.slice(startIndex, startIndex + this.pageSize)
            }
        },
        methods: {
            restorePagination() {
                const pagination = readListPagination('time')
                if (pagination != null) {
                    this.currentPage = pagination.currentPage
                    this.pageSize = pagination.pageSize
                }
            },
            restoreListQueryState() {
                const queryState = readListQueryState('time')
                if (queryState == null) {
                    return
                }
                this.currentPage = queryState.currentPage
                this.pageSize = queryState.pageSize
                this.searchContent = queryState.searchContent
            },
            saveListQueryState() {
                writeListQueryState('time', {
                    currentPage: this.currentPage,
                    pageSize: this.pageSize,
                    searchContent: this.searchContent
                })
            },
            savePagination() {
                writeListPagination('time', this.currentPage, this.pageSize)
            },
            async queryTimesByBilledStatus(billed) {
                const pageSize = 400
                const rows = []
                let currentPage = 1
                let totalCount = null
                do {
                    const response = await this.$axios.post('query_time_tracker_list_user_web', {
                        params: {
                            currentPage,
                            pageSize,
                            userWebCommonQuery: '',
                            orderName: this.settings.timeSortType === 0 ? 'clientName' : 'startDate',
                            orderTpe: this.settings.timeSortAscending === 1 ? 1 : 2,
                            companyUUID: this.company.innerUUID,
                            billed,
                            timeZone: getBrowserTimeZone()
                        }
                    })
                    if (response.data.errorCode !== 0) {
                        throw new Error(response.data.errorMsg)
                    }
                    if (!Array.isArray(response.data.dataList)
                        || !Number.isInteger(response.data.totalCount) || response.data.totalCount < 0) {
                        throw new Error('Time list response is invalid')
                    }
                    if (totalCount == null) {
                        totalCount = response.data.totalCount
                    } else if (totalCount !== response.data.totalCount) {
                        throw new Error('Time list changed while loading')
                    }
                    if (response.data.dataList.length === 0 && rows.length < totalCount) {
                        throw new Error('Time list pagination is incomplete')
                    }
                    rows.push(...response.data.dataList)
                    currentPage += 1
                } while (rows.length < totalCount)
                return rows
            },
            async queryTimes() {
                if (!this.contextLoaded || this.company == null) {
                    return
                }
                const requestSequence = ++this.timeQuerySequence
                this.loading = true
                this.loadError = ''
                try {
                    const result = await Promise.all([
                        this.queryTimesByBilledStatus(0),
                        this.queryTimesByBilledStatus(1)
                    ])
                    if (requestSequence !== this.timeQuerySequence) {
                        return
                    }
                    this.rawTimes = result[0].concat(result[1])
                } catch (error) {
                    reportHandledError(error, {
                        module: 'time',
                        operation: 'query_time_list'
                    })
                    if (requestSequence === this.timeQuerySequence) {
                        this.rawTimes = []
                        this.loadError = error.message
                    }
                } finally {
                    if (requestSequence === this.timeQuerySequence) {
                        this.loading = false
                    }
                }
            },
            resetPage() {
                this.currentPage = 1
                this.savePagination()
            },
            changePage(page) {
                this.currentPage = page
                this.savePagination()
            },
            changePageSize(pageSize) {
                this.currentPage = 1
                this.pageSize = pageSize
                this.savePagination()
            },
            goCreate() {
                if (!this.contextLoaded || this.loading) {
                    return
                }
                this.$router.push('/timeCreate')
            },
            handleRowOpen(row, column, event) {
                this.openTime(row, event)
            },
            openTime(row, event) {
                if (!this.contextLoaded || this.loading) {
                    return
                }
                openListDetail(this.$router, {path: '/timeUpdate', query: {innerUUID: row.innerUUID}}, event)
            },
            formatDate(timestamp) {
                return formatTimeListDate(timestamp, this.$i18n.locale)
            },
            formatDuration(minutes) {
                return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
            },
            money(value) {
                return xiaoshudianfenge(value, this.userSetting)
            },
            async loadPage() {
                this.loading = true
                this.contextLoaded = false
                this.loadError = ''
                try {
                    const response = await this.$axios.post('query_setting_and_company', {params: {}})
                    if (response.data.errorCode !== 0) {
                        throw new Error('Company and setting query failed')
                    }
                    const selectedData = syncCompanySelection(response.data)
                    if (selectedData == null) {
                        throw new Error('Selected company data is missing')
                    }
                    this.company = selectedData.company
                    this.userSetting = selectedData.setting
                    this.restoreListQueryState()
                    this.settings = {
                        timeSortType: this.company.timeSortType,
                        timeSortAscending: this.company.timeSortAscending
                    }
                    this.contextLoaded = true
                    await this.queryTimes()
                } catch (error) {
                    reportHandledError(error, {
                        module: 'time',
                        operation: 'load_page'
                    })
                    this.loadError = error.message
                    this.loading = false
                }
            }
        },
        async mounted() {
            this.restorePagination()
            await this.loadPage()
            this.$watch('searchContent', debounce(() => {
                if (!this.contextLoaded) {
                    return
                }
                this.currentPage = 1
                this.savePagination()
            }, 500))
        },
        beforeRouteLeave(to, from, next) {
            this.saveListQueryState()
            next()
        }
    }
</script>

<style scoped>
    .time-page-header { grid-template-columns: minmax(180px, 1fr) minmax(320px, 420px) minmax(180px, 1fr); }
    .time-page-header .list-page-search { justify-self: center; }
    .time-header-actions { display: flex; gap: 12px; justify-content: flex-end; }
    .time-client-name,
    .time-description,
    .time-rate-value { overflow: hidden; display: -webkit-box; -webkit-box-orient: vertical; overflow-wrap: anywhere; }
    .time-client-name { -webkit-line-clamp: 2; }
    .time-description { -webkit-line-clamp: 2; margin-top: 5px; color: #8a97aa; font-weight: 400; }
    .time-rate-value { -webkit-line-clamp: 3; }
    .time-date-value { white-space: nowrap; }
    .time-total-value { white-space: nowrap; font-variant-numeric: tabular-nums; }
    .time-mobile-list { display: none; }
    .time-empty { min-height: 430px; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #8a97aa; text-align: center; }
    .time-empty i { font-size: 54px; color: #aab6c8; }
    .time-empty h2 { margin: 18px 0 6px; color: #263653; }
    .time-empty p { margin: 0; }
    @media (max-width: 1200px) {
        .time-page-header { grid-template-columns: 1fr; height: auto !important; gap: 18px; }
        .time-header-actions { justify-content: flex-start; }
    }
    @media (max-width: 720px) {
        .time-desktop-table { display: none; }
        .time-mobile-list { display: grid; gap: 12px; }
        .time-mobile-card {
            width: 100%; padding: 18px; border: 1px solid #e6eaf0; border-radius: 14px;
            background: #fff; color: #263653; text-align: left; box-shadow: 0 6px 18px rgba(42, 61, 92, .06);
            font: inherit; cursor: pointer;
        }
        .time-mobile-card:focus-visible { outline: 3px solid rgba(64, 158, 255, .28); outline-offset: 2px; }
        .time-mobile-heading { display: flex; justify-content: space-between; gap: 16px; font-size: 16px; }
        .time-mobile-heading strong:first-child { min-width: 0; overflow-wrap: anywhere; }
        .time-mobile-heading strong:last-child { flex: none; font-variant-numeric: tabular-nums; }
        .time-mobile-description {
            display: -webkit-box; margin-top: 8px; overflow: hidden; color: #7b889d; overflow-wrap: anywhere;
            -webkit-box-orient: vertical; -webkit-line-clamp: 3;
        }
        .time-mobile-facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 16px; }
        .time-mobile-facts > span { min-width: 0; overflow-wrap: anywhere; }
        .time-mobile-facts small { display: block; margin-bottom: 4px; color: #8a97aa; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .time-mobile-rate-value {
            display: -webkit-box; overflow: hidden; overflow-wrap: anywhere;
            -webkit-box-orient: vertical; -webkit-line-clamp: 4;
        }
    }
    @media (max-width: 430px) {
        .time-mobile-facts { grid-template-columns: 1fr 1fr; }
        .time-mobile-facts > span:last-child { grid-column: 1 / -1; }
    }
</style>
